import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Paper,
    Chip,
    alpha,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import {
    Send as SendIcon,
    Schedule as ScheduleIcon,
    MenuBook as BookOpenIcon,
    Assignment as AssignmentIcon,
    TextFields as TextFieldsIcon,
    Code as CodeIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { coursesApi } from '../../../services/api';
import RichTextEditor from '../../common/RichTextEditor';

const AssignmentTab = ({ assignment, token }) => {
    const { t } = useTranslation();
    const [solution, setSolution] = useState('');
    const [editorMode, setEditorMode] = useState('rich'); // 'rich' or 'plain'
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [loadingSubmissions, setLoadingSubmissions] = useState(true);

    // Load submissions on mount
    useEffect(() => {
        const loadSubmissions = async () => {
            if (!assignment?.id || !token) return;

            setLoadingSubmissions(true);
            try {
                const data = await coursesApi.getAssignmentSubmissions(assignment.id, token);
                setSubmissions(data || []);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Error loading submissions:', err);
            } finally {
                setLoadingSubmissions(false);
            }
        };

        loadSubmissions();
    }, [assignment?.id, token]);

    const handleSubmit = async () => {
        if (!solution.trim()) {
            setError(t('assignment_tab.errors.empty_solution'));
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            await coursesApi.createSubmission(assignment.id, {
                content: solution
            }, token);

            // Refresh submissions list
            const updatedSubmissions = await coursesApi.getAssignmentSubmissions(
                assignment.id,
                token
            );
            setSubmissions(updatedSubmissions || []);
            setSolution(''); // Clear form
        } catch (err) {
            setError(err.message || t('assignment_tab.errors.submission_failed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditorModeChange = (event, newMode) => {
        if (newMode !== null) {
            setEditorMode(newMode);
        }
    };

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
            {/* Assignment Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <BookOpenIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {assignment.title}
                    </Typography>
                </Box>

                {assignment.deadline && (
                    <Alert
                        icon={<ScheduleIcon />}
                        severity="info"
                        sx={{ mb: 2 }}
                    >
                        {t('assignment_tab.deadline', {
                            date: new Date(assignment.deadline).toLocaleDateString()
                        })}
                    </Alert>
                )}

                <Box
                    sx={{
                        '& p': { margin: 0, marginBottom: 1 },
                        '& p:last-child': { marginBottom: 0 },
                        '& ul, & ol': { marginTop: 0.5, marginBottom: 0.5, paddingLeft: 3 },
                        '& li': { marginBottom: 0.5 },
                        '& code': {
                            bgcolor: alpha('#000', 0.05),
                            padding: '2px 6px',
                            borderRadius: 0.5,
                            fontFamily: 'monospace',
                            fontSize: '0.9em'
                        },
                        '& pre': {
                            bgcolor: alpha('#000', 0.05),
                            padding: 2,
                            borderRadius: 1,
                            overflow: 'auto',
                            marginTop: 1,
                            marginBottom: 1
                        },
                        '& pre code': {
                            bgcolor: 'transparent',
                            padding: 0
                        }
                    }}
                >
                    <ReactMarkdown>{assignment.description}</ReactMarkdown>
                </Box>

                {assignment.correct_answer && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: alpha('#2196F3', 0.05),
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: alpha('#2196F3', 0.2)
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                            {t('assignment_tab.acceptance_criteria')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {assignment.correct_answer}
                        </Typography>
                    </Box>
                )}

                {/* Assignment metadata */}
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    {assignment.points && (
                        <Chip
                            label={t('assignment_tab.max_points', { points: assignment.points })}
                            size="small"
                            color="warning"
                            variant="outlined"
                        />
                    )}
                </Box>
            </Paper>

            {/* Submission Form */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AssignmentIcon sx={{ color: 'success.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('assignment_tab.submit_solution')}
                    </Typography>
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {/* History of submissions and feedbacks */}
                {submissions.length > 0 && (
                    <Box sx={{ mb: 3, maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
                        {submissions.slice().reverse().map((submission) => (
                            <Box key={submission.id} sx={{ mb: 2 }}>
                                {/* Student's answer (right side, blue) */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                    <Box
                                        sx={{
                                            maxWidth: '70%',
                                            bgcolor: alpha('#2196F3', 0.1),
                                            borderRadius: 2,
                                            p: 2,
                                            position: 'relative'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                '& p': { margin: 0, marginBottom: 1 },
                                                '& p:last-child': { marginBottom: 0 },
                                                '& ul, & ol': { marginTop: 0.5, marginBottom: 0.5, paddingLeft: 3 },
                                                '& li': { marginBottom: 0.5 },
                                                '& code': {
                                                    bgcolor: alpha('#000', 0.05),
                                                    padding: '2px 6px',
                                                    borderRadius: 0.5,
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.9em'
                                                },
                                                '& pre': {
                                                    bgcolor: alpha('#000', 0.05),
                                                    padding: 2,
                                                    borderRadius: 1,
                                                    overflow: 'auto',
                                                    marginTop: 1,
                                                    marginBottom: 1
                                                },
                                                '& pre code': {
                                                    bgcolor: 'transparent',
                                                    padding: 0
                                                }
                                            }}
                                            dangerouslySetInnerHTML={{ __html: submission.content }}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
                                            {new Date(submission.submitted_at).toLocaleString()}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Feedback (left side, green) */}
                                {submission.feedback && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                                        <Box
                                            sx={{
                                                maxWidth: '70%',
                                                bgcolor: alpha('#4CAF50', 0.1),
                                                borderRadius: 2,
                                                p: 2,
                                                position: 'relative'
                                            }}
                                        >
                                            <Box sx={{
                                                '& p': { margin: 0, marginBottom: 1 },
                                                '& p:last-child': { marginBottom: 0 }
                                            }}>
                                                <ReactMarkdown>{submission.feedback}</ReactMarkdown>
                                            </Box>
                                            {submission.feedback_at && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                    {new Date(submission.feedback_at).toLocaleString()}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Check if max attempts reached */}
                {assignment.max_attempts && submissions.length >= assignment.max_attempts ? (
                    <Alert severity="warning">
                        {t('assignment_tab.max_attempts_reached')}
                    </Alert>
                ) : (
                    <Box>
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                <ToggleButtonGroup
                                    value={editorMode}
                                    exclusive
                                    onChange={handleEditorModeChange}
                                    size="small"
                                    aria-label="editor mode"
                                >
                                    <ToggleButton value="rich" aria-label="rich text editor">
                                        <TextFieldsIcon fontSize="small" sx={{ mr: 0.5 }} />
                                        {t('assignment_tab.rich_editor')}
                                    </ToggleButton>
                                    <ToggleButton value="plain" aria-label="plain text editor">
                                        <CodeIcon fontSize="small" sx={{ mr: 0.5 }} />
                                        {t('assignment_tab.plain_editor')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {editorMode === 'rich' ? (
                                <RichTextEditor
                                    value={solution}
                                    onChange={setSolution}
                                    placeholder={t('assignment_tab.solution_placeholder')}
                                    minHeight="450px"
                                />
                            ) : (
                                <Box
                                    component="textarea"
                                    value={solution}
                                    onChange={(e) => setSolution(e.target.value)}
                                    placeholder={t('assignment_tab.solution_placeholder_plain')}
                                    sx={{
                                        width: '100%',
                                        minHeight: '450px',
                                        p: 2,
                                        fontFamily: 'monospace',
                                        fontSize: '14px',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        resize: 'vertical',
                                        '&:focus': {
                                            outline: 'none',
                                            borderColor: 'primary.main',
                                        }
                                    }}
                                />
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                                {t('assignment_tab.submissions_count', { count: submissions.length })}
                                {assignment.max_attempts && ` / ${assignment.max_attempts}`}
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isSubmitting || !solution.trim()}
                                startIcon={isSubmitting ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    <SendIcon />
                                )}
                                sx={{
                                    bgcolor: 'success.main',
                                    '&:hover': {
                                        bgcolor: 'success.dark'
                                    }
                                }}
                            >
                                {isSubmitting
                                    ? t('assignment_tab.submitting')
                                    : t('assignment_tab.submit_button')}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* Loading State */}
            {loadingSubmissions && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
};

export default AssignmentTab;
